/**
 * Supabase Auth Provider
 *
 * Implements the AuthProvider interface using Supabase authentication.
 * This is the default provider for the current implementation.
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  User,
  Session,
  AuthError as SupabaseAuthError,
  UserAttributes,
} from '@supabase/supabase-js';
import type {
  AuthProvider,
  AuthUser,
  AuthSession,
  AuthError,
  SignInResult,
  SignUpResult,
  AuthStateChangeCallback,
  AuthEvent,
  ResetPasswordOptions,
} from '../types';

function mapSupabaseUser(user: User | null): AuthUser | null {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email || '',
    emailConfirmed: user.email_confirmed_at != null,
    metadata: user.user_metadata,
    createdAt: user.created_at,
  };
}

function mapSupabaseSession(session: Session | null): AuthSession | null {
  if (!session) return null;
  return {
    user: mapSupabaseUser(session.user)!,
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    expiresAt: session.expires_at,
  };
}

function mapSupabaseError(error: SupabaseAuthError | null): AuthError | null {
  if (!error) return null;
  return {
    message: error.message || 'An unknown error occurred',
    code: error.code,
    status: error.status,
  };
}

function mapSupabaseEvent(event: string): AuthEvent {
  const eventMap: Record<string, AuthEvent> = {
    SIGNED_IN: 'SIGNED_IN',
    SIGNED_OUT: 'SIGNED_OUT',
    TOKEN_REFRESHED: 'TOKEN_REFRESHED',
    USER_UPDATED: 'USER_UPDATED',
    PASSWORD_RECOVERY: 'PASSWORD_RECOVERY',
  };
  return eventMap[event] || 'SIGNED_IN';
}

export class SupabaseAuthProvider implements AuthProvider {
  private cachedUser: AuthUser | null = null;

  async signInWithPassword(email: string, password: string): Promise<SignInResult> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (data?.session) {
      this.cachedUser = mapSupabaseUser(data.session.user);
    }

    return {
      session: mapSupabaseSession(data?.session),
      error: mapSupabaseError(error),
    };
  }

  async signUp(email: string, password: string): Promise<SignUpResult> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (data?.user) {
      this.cachedUser = mapSupabaseUser(data.user);
    }

    return {
      user: mapSupabaseUser(data?.user),
      session: mapSupabaseSession(data?.session),
      error: mapSupabaseError(error),
    };
  }

  async signOut(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signOut();
    this.cachedUser = null;
    return { error: mapSupabaseError(error) };
  }

  async getSession(): Promise<{ session: AuthSession | null; error: AuthError | null }> {
    const { data, error } = await supabase.auth.getSession();

    if (data?.session) {
      this.cachedUser = mapSupabaseUser(data.session.user);
    }

    return {
      session: mapSupabaseSession(data?.session),
      error: mapSupabaseError(error),
    };
  }

  async refreshSession(): Promise<{ session: AuthSession | null; error: AuthError | null }> {
    const { data, error } = await supabase.auth.refreshSession();

    if (data?.session) {
      this.cachedUser = mapSupabaseUser(data.session.user);
    }

    return {
      session: mapSupabaseSession(data?.session),
      error: mapSupabaseError(error),
    };
  }

  onAuthStateChange(callback: AuthStateChangeCallback): () => void {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const mappedEvent = mapSupabaseEvent(event);
      const mappedSession = mapSupabaseSession(session);

      if (mappedSession) {
        this.cachedUser = mappedSession.user;
      } else {
        this.cachedUser = null;
      }

      callback(mappedEvent, mappedSession);
    });

    return () => subscription.unsubscribe();
  }

  getCurrentUser(): AuthUser | null {
    return this.cachedUser;
  }

  async resetPasswordForEmail(
    email: string,
    options?: ResetPasswordOptions,
  ): Promise<{ error: AuthError | null }> {
    // Forwarded as-is: when it is undefined supabase-js falls back to the Site URL
    // configured in the dashboard, which is the old behaviour.
    const { error } = await supabase.auth.resetPasswordForEmail(email, options);
    return { error: mapSupabaseError(error) };
  }

  async updateUser(data: {
    email?: string;
    password?: string;
    metadata?: Record<string, unknown>;
  }): Promise<{ user: AuthUser | null; error: AuthError | null }> {
    const updateData: UserAttributes = {};
    if (data.email) updateData.email = data.email;
    if (data.password) updateData.password = data.password;
    if (data.metadata) updateData.data = data.metadata;

    const { data: result, error } = await supabase.auth.updateUser(updateData);

    if (result?.user) {
      this.cachedUser = mapSupabaseUser(result.user);
    }

    return {
      user: mapSupabaseUser(result?.user),
      error: mapSupabaseError(error),
    };
  }
}

export const supabaseAuthProvider = new SupabaseAuthProvider();
